import React from "react";
import { motion } from "framer-motion";
import { MessagesSquare, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Panel, Spinner, SectionTitle, Button } from "@/components/ui";
import MemoryCard from "@/components/MemoryCard";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Conversations() {
  const { active, loading } = useBusiness();
  const [list, setList] = React.useState(null);
  const [sel, setSel] = React.useState(null);
  const [thread, setThread] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!active) return;
    const { data } = await api.get(`/businesses/${active.id}/conversations`);
    setList(data);
    if (data.length && !sel) openThread(data[0].waId);
  }, [active]); // eslint-disable-line

  React.useEffect(() => { load(); }, [load]);

  const openThread = async (waId) => {
    setSel(waId); setThread(null);
    const { data } = await api.get(`/businesses/${active.id}/conversations/${waId}/thread`);
    setThread(data);
  };

  if (loading) return null;
  if (!active) return <Panel className="p-8 text-center text-sm" style={{ color: "var(--text-2)" }}>Pick a workspace.</Panel>;

  return (
    <div>
      <SectionTitle eyebrow={active.name} title="Chats">
        <Button variant="ghost" size="sm" onClick={load} data-testid="convos-refresh"><RefreshCw size={14} /> Refresh</Button>
      </SectionTitle>

      {list && list.length === 0 && (
        <Panel className="p-10 text-center" data-testid="convos-empty">
          <MessagesSquare size={28} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-2)" }}>No conversations yet. Once customers message your WhatsApp number, their chats appear here.</p>
        </Panel>
      )}

      {(!list || list.length > 0) && (
        <div className="grid lg:grid-cols-[320px_1fr] gap-px" style={{ background: "var(--border)" }}>
          {/* list */}
          <div style={{ background: "var(--s1)" }} className="max-h-[70vh] overflow-y-auto" data-testid="convos-list">
            {!list && <div className="p-4"><Spinner /></div>}
            {list && list.map((c) => (
              <button key={c.waId} onClick={() => openThread(c.waId)} data-testid={`convo-${c.waId}`}
                className="w-full text-left p-4 transition-colors"
                style={{ borderBottom: "1px solid var(--border)", background: sel === c.waId ? "var(--s2)" : "transparent" }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{c.name || `+${c.waId}`}</span>
                  <span className="mono text-[10px]" style={{ color: "var(--text-muted)" }}>{timeAgo(c.lastAt)}</span>
                </div>
                <div className="text-xs mt-1 truncate" style={{ color: "var(--text-muted)" }}>
                  {c.lastDirection === "out" ? "↩ " : ""}{c.lastMessage}
                </div>
              </button>
            ))}
          </div>

          {/* thread */}
          <div style={{ background: "var(--s1)" }} className="p-5 max-h-[70vh] overflow-y-auto" data-testid="convo-thread">
            {!thread ? <div className="flex items-center justify-center h-40"><Spinner /></div> : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm font-medium">+{thread.waId}</div>
                  <span className="mono text-[10px]" style={{ color: "var(--text-muted)" }}>{thread.messages.length} messages</span>
                </div>
                <div className="space-y-2 mb-6">
                  {thread.messages.map((m, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.01 }}
                      className="flex" style={{ justifyContent: m.direction === "in" ? "flex-start" : "flex-end" }}>
                      <div className="max-w-[78%] px-3 py-2 text-sm" style={{
                        background: m.direction === "in" ? "var(--s2)" : "var(--accent-2)",
                        color: m.direction === "in" ? "var(--text-2)" : "#11150b", whiteSpace: "pre-wrap" }}>
                        {m.body}
                      </div>
                    </motion.div>
                  ))}
                </div>
                {thread.memory && <MemoryCard memory={thread.memory} />}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
